import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MegaphoneIcon } from 'lucide-react';
import { Button } from '@tinct/web-components';
import { Collection } from '@tinct/web-collection';
import { useGetCampaignPages, useGetCampaignById } from '@tinct/core-api';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';
import { useLandingPagesColumns } from './hooks/use-landing-pages-columns';
import { useAdsIntegrations } from '../../../hooks/use-ads-integrations';

import { PageStatusBadge } from '../../shared';
import { GenerateAdPageDialog } from './generate-ad-page-dialog';

export function LandingPagesTab() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const [adDialogOpen, setAdDialogOpen] = useState(false);

  // The "generate from ad campaign" CTA only exists with an ads feature flag
  // on AND a READY ads integration (Google Ads or LinkedIn Ads).
  const { integrations: adsIntegrations, providerByIntegrationId } = useAdsIntegrations();
  const { data: campaign } = useGetCampaignById({
    id,
    fields: {
      id: null,
      name: null,
      status: null,
      published: null,
      landingPageVariationAxis: null,
      landingPage: {
        fields: {
          url: null,
        },
      },
      snippetStatus: null,
      pages: { fields: { id: null } },
      statistics: {
        fields: {
          totalPagesCount: null,
          publishedPagesCount: null,
        },
      },
      createdAt: null,
      updatedAt: null,
    },
  });

  const columns = useLandingPagesColumns({ campaign, providerByIntegrationId });

  // The manual generate endpoint only accepts ADWORD-axis campaigns (400
  // otherwise); variants normally generate automatically on ads-sync match,
  // the dialog is a retry/fallback.
  const isAdwordAxis = campaign?.landingPageVariationAxis === 'adword';
  const canGenerateAdPage = adsIntegrations.length > 0 && isAdwordAxis;

  const publishedCount = campaign?.statistics?.publishedPagesCount ?? 0;
  const totalCount = campaign?.statistics?.totalPagesCount ?? 0;
  const notPublishedCount = totalCount - publishedCount;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold tracking-tight font-heading">All pages</h2>
        <div className="flex items-center gap-1">
          <PageStatusBadge status="published" label={`${publishedCount} published`} />
          <PageStatusBadge status="unpublished" label={`${notPublishedCount} not published`} />
        </div>
      </div>

      {/* Collection */}
      <Collection
        useLocationAsState
        columns={columns}
        // Live-update rows while any page is generating. ADWORD campaigns also
        // poll while idle: their variants generate automatically on ads-sync
        // match, with no local trigger to key on.
        refetchInterval={(query) => {
          const rows = query.state.data?.data ?? [];
          const hasInflight = rows.some(
            (p) =>
              p.status === 'generating' ||
              p.status === 'queued' ||
              p.status === 'waiting',
          );
          if (hasInflight) return 3000;
          return isAdwordAxis ? 5000 : false;
        }}
        // The variant dashboard only exists once the page reached the
        // published/unpublished states — same rule as the row's chart icon.
        onRowClick={(page) => {
          if (!page.id) return;
          if (page.status !== 'published' && page.status !== 'unpublished') return;
          navigate(`${workspaceUrl}/campaigns/${id}/landing-pages/${page.id}`);
        }}
        filtersConfiguration={{
          textFilter: {
            placeholder: 'Select a page',
            queryParamName: 'term',
          },
        }}
        toolbarActions={
          canGenerateAdPage ? (
            <Button variant="outline" onClick={() => setAdDialogOpen(true)}>
              <MegaphoneIcon className="size-4" />
              Generate from ad campaign
            </Button>
          ) : undefined
        }
        useQueryFn={useGetCampaignPages}
        staticParams={{
          campaignId: id,
        }}
        queryFields={{
          id: null,
          status: null,
          outdatedStatus: null,
          staticUrl: null,
          targetType: null,
          adCampaign: { fields: { id: null, name: null, integrationId: null } },
          // Feeds getEffectiveStatus — the badge shows the live job state.
          lastGenerationJob: {
            fields: {
              status: null,
            },
          },
          lead: {
            fields: {
              id: null,
              tag: null,
              company: {
                fields: {
                  id: null,
                  logo: null,
                  name: null,
                }
              }
            },
          },
          campaign: {
            fields: {
              id: null,
              name: null,
            },
          },
          statistics: {
            fields: {
              visitsCount: null,
            },
          },
          createdAt: null,
          lastPublishedAt: null,
        }}
        emptyState={
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-sm font-medium text-foreground">No pages yet</p>
            <p className="text-sm text-muted-foreground">
              Pages will appear here once generated.
            </p>
          </div>
        }
      />

      {canGenerateAdPage && (
        <GenerateAdPageDialog
          campaignId={id}
          adsIntegrations={adsIntegrations}
          open={adDialogOpen}
          onOpenChange={setAdDialogOpen}
        />
      )}
    </div>
  );
}
